import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from './api';
import useAuth from '../hooks/useAuth';
import { useDialog } from './useDialog';
import './TherapistDashboard.css';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

// ── Utility ───────────────────────────────────────────────────────────────────
function deepSet(obj, keys, val) {
  if (keys.length === 0) return val;
  const [head, ...rest] = keys;
  return { ...(obj || {}), [head]: deepSet((obj || {})[head], rest, val) };
}

// ── Static data definitions ───────────────────────────────────────────────────
export const ROM_PARTS = [
  {
    key: 'cervical', label: 'Neck (Cervical)', icon: '🔵', bilateral: false,
    movements: [
      { key: 'flexion',          label: 'Flexion',               normal: 45  },
      { key: 'extension',        label: 'Extension',             normal: 45  },
      { key: 'leftLateralFlex',  label: 'Lt. Lateral Flexion',   normal: 45  },
      { key: 'rightLateralFlex', label: 'Rt. Lateral Flexion',   normal: 45  },
      { key: 'leftRotation',     label: 'Left Rotation',         normal: 80  },
      { key: 'rightRotation',    label: 'Right Rotation',        normal: 80  },
    ],
  },
  {
    key: 'thoracic', label: 'Thoracic Spine (Upper-Mid Back)', icon: '🔶', bilateral: false,
    movements: [
      { key: 'flexion',          label: 'Flexion',               normal: 50  },
      { key: 'extension',        label: 'Extension',             normal: 25  },
      { key: 'leftLateralFlex',  label: 'Lt. Lateral Flexion',   normal: 25  },
      { key: 'rightLateralFlex', label: 'Rt. Lateral Flexion',   normal: 25  },
      { key: 'leftRotation',     label: 'Left Rotation',         normal: 35  },
      { key: 'rightRotation',    label: 'Right Rotation',        normal: 35  },
    ],
  },
  {
    key: 'shoulder', label: 'Shoulder', icon: '💪', bilateral: true,
    movements: [
      { key: 'flexion',           label: 'Flexion',              normal: 180 },
      { key: 'extension',         label: 'Extension',            normal: 60  },
      { key: 'abduction',         label: 'Abduction',            normal: 180 },
      { key: 'adduction',         label: 'Adduction',            normal: 30  },
      { key: 'internalRotation',  label: 'Internal Rotation',    normal: 70  },
      { key: 'externalRotation',  label: 'External Rotation',    normal: 90  },
      { key: 'horizAbduction',    label: 'Horizontal Abduction', normal: 90  },
      { key: 'horizAdduction',    label: 'Horizontal Adduction', normal: 135 },
    ],
  },
  {
    key: 'elbow', label: 'Elbow / Forearm', icon: '🦾', bilateral: true,
    movements: [
      { key: 'flexion',    label: 'Flexion',    normal: 145 },
      { key: 'extension',  label: 'Extension',  normal: 0   },
      { key: 'supination', label: 'Supination', normal: 90  },
      { key: 'pronation',  label: 'Pronation',  normal: 90  },
    ],
  },
  {
    key: 'wrist', label: 'Wrist', icon: '✋', bilateral: true,
    movements: [
      { key: 'flexion',   label: 'Flexion',          normal: 80 },
      { key: 'extension', label: 'Extension',        normal: 70 },
      { key: 'radialDev', label: 'Radial Deviation', normal: 20 },
      { key: 'ulnarDev',  label: 'Ulnar Deviation',  normal: 30 },
    ],
  },
  {
    key: 'lowerBack', label: 'Lumbar Spine (Lower Back)', icon: '🔺', bilateral: false,
    movements: [
      { key: 'flexion',          label: 'Flexion',               normal: 90 },
      { key: 'extension',        label: 'Extension',             normal: 25 },
      { key: 'leftLateralFlex',  label: 'Lt. Lateral Flexion',   normal: 35 },
      { key: 'rightLateralFlex', label: 'Rt. Lateral Flexion',   normal: 35 },
      { key: 'leftRotation',     label: 'Left Rotation',         normal: 35 },
      { key: 'rightRotation',    label: 'Right Rotation',        normal: 35 },
    ],
  },
  {
    key: 'hip', label: 'Hip', icon: '🦋', bilateral: true,
    movements: [
      { key: 'flexion',           label: 'Flexion',              normal: 120 },
      { key: 'extension',         label: 'Extension',            normal: 30  },
      { key: 'abduction',         label: 'Abduction',            normal: 45  },
      { key: 'adduction',         label: 'Adduction',            normal: 20  },
      { key: 'internalRotation',  label: 'Internal Rotation',    normal: 45  },
      { key: 'externalRotation',  label: 'External Rotation',    normal: 45  },
    ],
  },
  {
    key: 'knee', label: 'Knee', icon: '🦵', bilateral: true,
    movements: [
      { key: 'flexion',           label: 'Flexion',          normal: 135 },
      { key: 'extension',         label: 'Extension',        normal: 0   },
      { key: 'internalRotation',  label: 'Internal Rotation (at 90° flex)', normal: 30 },
      { key: 'externalRotation',  label: 'External Rotation (at 90° flex)', normal: 40 },
    ],
  },
  {
    key: 'ankle', label: 'Ankle / Foot', icon: '🦶', bilateral: true,
    movements: [
      { key: 'dorsiflexion',   label: 'Dorsiflexion',    normal: 20 },
      { key: 'plantarFlexion', label: 'Plantar Flexion',  normal: 50 },
      { key: 'inversion',      label: 'Inversion',        normal: 35 },
      { key: 'eversion',       label: 'Eversion',         normal: 15 },
    ],
  },
];

export const MUSCLE_PARTS = [
  // ── Neck ──────────────────────────────────────────────────────────────────
  {
    key: 'sternocleidomastoid', label: 'Sternocleidomastoid', icon: '🔵', bilateral: true,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  {
    key: 'trapezius', label: 'Trapezius', icon: '🔵', bilateral: true,
    groups: [
      { key: 'upper', label: 'Upper Trapezius' },
      { key: 'middle', label: 'Middle Trapezius' },
      { key: 'lower', label: 'Lower Trapezius' },
    ],
  },
  // ── Shoulder / Upper Arm ──────────────────────────────────────────────────
  {
    key: 'deltoid', label: 'Deltoid', icon: '💪', bilateral: true,
    groups: [
      { key: 'anterior', label: 'Anterior Deltoid' },
      { key: 'middle',   label: 'Middle Deltoid' },
      { key: 'posterior', label: 'Posterior Deltoid' },
    ],
  },
  {
    key: 'rotatorCuff', label: 'Rotator Cuff', icon: '💪', bilateral: true,
    groups: [
      { key: 'supraspinatus',  label: 'Supraspinatus' },
      { key: 'infraspinatus',  label: 'Infraspinatus' },
      { key: 'subscapularis',  label: 'Subscapularis' },
      { key: 'teresMajor',     label: 'Teres Major' },
    ],
  },
  {
    key: 'pectoralisMajor', label: 'Pectoralis Major', icon: '💪', bilateral: true,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  {
    key: 'biceps', label: 'Biceps Brachii', icon: '🦾', bilateral: true,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  {
    key: 'triceps', label: 'Triceps Brachii', icon: '🦾', bilateral: true,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  // ── Forearm / Hand ────────────────────────────────────────────────────────
  {
    key: 'wristFlexors', label: 'Wrist Flexors (FCR / FCU)', icon: '✋', bilateral: true,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  {
    key: 'wristExtensors', label: 'Wrist Extensors (ECRB / ECU)', icon: '✋', bilateral: true,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  {
    key: 'handGrip', label: 'Hand Grip', icon: '✋', bilateral: true,
    groups: [{ key: 'overall', label: 'Grip Strength' }],
  },
  // ── Trunk / Core ──────────────────────────────────────────────────────────
  {
    key: 'rectusAbdominis', label: 'Rectus Abdominis', icon: '🔺', bilateral: false,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  {
    key: 'obliques', label: 'Obliques', icon: '🔺', bilateral: true,
    groups: [
      { key: 'internal', label: 'Internal Oblique' },
      { key: 'external', label: 'External Oblique' },
    ],
  },
  {
    key: 'erectorSpinae', label: 'Erector Spinae', icon: '🔺', bilateral: false,
    groups: [
      { key: 'thoracic', label: 'Thoracic Region' },
      { key: 'lumbar',   label: 'Lumbar Region' },
    ],
  },
  {
    key: 'multifidus', label: 'Multifidus', icon: '🔺', bilateral: false,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  // ── Hip / Pelvis ──────────────────────────────────────────────────────────
  {
    key: 'gluteusMaximus', label: 'Gluteus Maximus', icon: '🦋', bilateral: true,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  {
    key: 'gluteusMedius', label: 'Gluteus Medius', icon: '🦋', bilateral: true,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  {
    key: 'iliopsoas', label: 'Iliopsoas (Hip Flexor)', icon: '🦋', bilateral: true,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  {
    key: 'adductors', label: 'Hip Adductors', icon: '🦋', bilateral: true,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  // ── Thigh ─────────────────────────────────────────────────────────────────
  {
    key: 'quadriceps', label: 'Quadriceps', icon: '🦵', bilateral: true,
    groups: [
      { key: 'rectusFemoris',  label: 'Rectus Femoris' },
      { key: 'vastusMedialis', label: 'Vastus Medialis' },
      { key: 'vastusLateralis', label: 'Vastus Lateralis' },
    ],
  },
  {
    key: 'hamstrings', label: 'Hamstrings', icon: '🦵', bilateral: true,
    groups: [
      { key: 'bicepsFemoris',  label: 'Biceps Femoris' },
      { key: 'semitendinosus', label: 'Semitendinosus' },
      { key: 'semimembranosus', label: 'Semimembranosus' },
    ],
  },
  // ── Lower Leg ─────────────────────────────────────────────────────────────
  {
    key: 'gastrocnemius', label: 'Gastrocnemius', icon: '🦶', bilateral: true,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  {
    key: 'soleus', label: 'Soleus', icon: '🦶', bilateral: true,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  {
    key: 'tibialisAnterior', label: 'Tibialis Anterior', icon: '🦶', bilateral: true,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
  {
    key: 'peroneals', label: 'Peroneals (Fibularis)', icon: '🦶', bilateral: true,
    groups: [{ key: 'overall', label: 'Overall Strength' }],
  },
];

// ── Helpers: derive side + romKey from BPConfig annotation ──
// romKey comes from the annotation itself (set via BPConfig editor); side from the key suffix
function deriveSide(bpKey) {
  if (bpKey.endsWith('_LB'))      return 'left';
  if (bpKey.endsWith('_RB'))      return 'right';
  if (bpKey.endsWith('_L'))       return 'left';
  if (bpKey.endsWith('_R'))       return 'right';
  return null;
}

// Build lookup maps from BPConfig annotations (romKey + muscles are stored on each annotation)
function buildBpMaps(allBp) {
  const romKeyMap = {};   // bpKey → romKey
  const musclesMap = {};  // bpKey → [muscleKey, ...]
  allBp.forEach(a => {
    if (a.romKey)  romKeyMap[a.key] = a.romKey;
    if (a.muscles && a.muscles.length > 0) musclesMap[a.key] = a.muscles;
  });
  return { romKeyMap, musclesMap };
}

// Build MUSCLE_LOOKUP from muscleParts array
function buildMuscleLookup(parts) {
  const lookup = {};
  (parts || []).forEach(m => { lookup[m.key] = m; });
  return lookup;
}

const MVC_OPTIONS = [
  { value: '',  label: '—'                     },
  { value: '0', label: '0 – No contraction'     },
  { value: '1', label: '1 – Trace / flicker'    },
  { value: '2', label: '2 – Gravity eliminated' },
  { value: '3', label: '3 – Against gravity'    },
  { value: '4', label: '4 – Some resistance'    },
  { value: '5', label: '5 – Normal'             },
];

const MVC_BG    = { '': '#f5f5f5', '0': '#ffebee', '1': '#fff3e0', '2': '#fff8e1', '3': '#e8f5e9', '4': '#e3f2fd', '5': '#e8eaf6' };
const MVC_COLOR = { '': '#888',    '0': '#c62828', '1': '#e65100', '2': '#f57f17', '3': '#2e7d32', '4': '#1565c0', '5': '#283593' };

const PART_COLORS = [
  '#2196f3', '#9c27b0', '#ff9800', '#e91e63',
  '#00897b', '#5c6bc0', '#f4511e', '#43a047',
];

/* ── Body-map dot positions for part selection ─────────── */
const ROM_FRONT_DOTS = [
  { partKey: 'cervical', side: null,    label: 'Cervical', cx: 195, cy: 62  },
  { partKey: 'shoulder', side: 'left',  label: 'L Shld',   cx: 145, cy: 115 },
  { partKey: 'shoulder', side: 'right', label: 'R Shld',   cx: 248, cy: 115 },
  { partKey: 'elbow',    side: 'left',  label: 'L Elbow',  cx: 118, cy: 175 },
  { partKey: 'elbow',    side: 'right', label: 'R Elbow',  cx: 272, cy: 175 },
  { partKey: 'wrist',    side: 'left',  label: 'L Wrist',  cx: 98,  cy: 240 },
  { partKey: 'wrist',    side: 'right', label: 'R Wrist',  cx: 292, cy: 240 },
  { partKey: 'knee',     side: 'left',  label: 'L Knee',   cx: 158, cy: 338 },
  { partKey: 'knee',     side: 'right', label: 'R Knee',   cx: 232, cy: 338 },
  { partKey: 'ankle',    side: 'left',  label: 'L Ankle',  cx: 158, cy: 438 },
  { partKey: 'ankle',    side: 'right', label: 'R Ankle',  cx: 232, cy: 438 },
];
const ROM_BACK_DOTS = [
  { partKey: 'lowerBack', side: null,    label: 'L.Back', cx: 195, cy: 215 },
  { partKey: 'hip',       side: 'left',  label: 'L Hip',  cx: 162, cy: 265 },
  { partKey: 'hip',       side: 'right', label: 'R Hip',  cx: 228, cy: 265 },
];

const thS = {
  padding: '8px 10px', fontWeight: 600, fontSize: 12,
  textAlign: 'left', color: '#555', borderBottom: '2px solid #e8edf5',
  whiteSpace: 'nowrap',
};
const tdS = {
  padding: '7px 10px', borderBottom: '1px solid #f0f0f4', verticalAlign: 'middle',
};

// ── Body SVG components — medical anatomy atlas style ────────────────────────
function RomFrontBodySVG() {
  const SKIN = "rgba(230,185,148,0.12)";
  const SSTR = "rgba(205,162,118,0.38)";
  const BONE = "rgba(238,222,182,0.90)";
  const BSTR = "rgba(205,188,140,0.88)";
  const MUS  = "rgba(186,52,32,0.68)";
  const MSTR = "rgba(145,28,16,0.60)";
  const TEND = "rgba(218,178,60,0.82)";
  const CART = "rgba(110,188,225,0.70)";
  const NERV = "rgba(252,214,28,0.80)";
  return (
    <>
      <defs>
        <radialGradient id="romBgF" cx="50%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#18263a"/>
          <stop offset="100%" stopColor="#0a1018"/>
        </radialGradient>
        <clipPath id="romTcF"><rect x="147" y="88" width="98" height="145"/></clipPath>
        <filter id="romNgF" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="1.8" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      {/* Background */}
      <rect x="0" y="0" width="390" height="490" fill="url(#romBgF)"/>

      {/* ── Skin silhouette ── */}
      <ellipse cx="195" cy="34" rx="26" ry="30" fill={SKIN} stroke={SSTR} strokeWidth="1.2"/>
      <rect x="185" y="62" width="20" height="24" rx="4" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M152,84 C145,100 140,160 142,198 C144,220 160,240 195,242 C230,240 246,220 248,198 C250,160 245,100 238,84 C228,78 210,74 195,74 C180,74 162,78 152,84Z" fill={SKIN} stroke={SSTR} strokeWidth="1"/>
      {/* Arms */}
      <path d="M152,90 C138,108 122,148 112,192 C108,210 110,242 120,242 C130,242 132,210 134,192 L148,144Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M238,90 C252,108 268,148 278,192 C282,210 280,242 270,242 C260,242 258,210 256,192 L242,144Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M108,242 C102,256 100,272 102,290 L112,294 L122,244Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M282,242 C288,256 290,272 288,290 L278,294 L268,244Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Pelvis + legs */}
      <path d="M155,200 C150,222 150,248 158,268 L192,270 L198,270 L232,268 C240,248 240,222 235,200Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M158,268 C152,290 150,324 152,352 C154,368 162,374 174,370 L180,312 L192,268Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M154,356 C150,382 150,416 152,444 C154,456 164,462 176,458 L180,426 L180,360Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M232,268 C238,290 240,324 238,352 C236,368 228,374 216,370 L210,312 L198,268Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M236,356 C240,382 240,416 238,444 C236,456 226,462 214,458 L210,426 L210,360Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>

      {/* ── Skeleton ── */}
      {/* Skull */}
      <ellipse cx="195" cy="30" rx="22" ry="25" fill="none" stroke={BONE} strokeWidth="1.2"/>
      <path d="M176,42 Q195,58 214,42" fill="none" stroke={BONE} strokeWidth="1.2"/>
      <ellipse cx="186" cy="30" rx="5" ry="4" fill="none" stroke={BONE} strokeWidth="0.9"/>
      <ellipse cx="204" cy="30" rx="5" ry="4" fill="none" stroke={BONE} strokeWidth="0.9"/>
      {/* Cervical vertebrae */}
      <rect x="191" y="64" width="8" height="5" rx="1" fill={BONE} stroke={BSTR} strokeWidth="0.5"/>
      <rect x="191" y="70" width="8" height="5" rx="1" fill={BONE} stroke={BSTR} strokeWidth="0.5"/>
      <rect x="191" y="76" width="8" height="5" rx="1" fill={BONE} stroke={BSTR} strokeWidth="0.5"/>
      {/* Clavicles */}
      <path d="M191,84 C182,86 170,88 158,92" stroke={BONE} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      <path d="M199,84 C208,86 220,88 232,92" stroke={BONE} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      {/* Sternum + xiphoid */}
      <rect x="192" y="90" width="6" height="55" rx="2" fill={BONE} stroke={BSTR} strokeWidth="0.5"/>
      <path d="M192,145 L195,155 L198,145" fill={BONE} stroke={BSTR} strokeWidth="0.5"/>
      {/* Ribs L */}
      <path d="M192,95  C180,96  166,100 160,112" fill="none" stroke={BONE} strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M192,105 C180,108 165,114 160,126" fill="none" stroke={BONE} strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M192,115 C180,118 165,124 162,136" fill="none" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M192,125 C180,128 166,132 164,142" fill="none" stroke={BONE} strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M160,112 C156,118 155,126 160,126" fill="none" stroke={CART} strokeWidth="1.2"/>
      <path d="M160,126 C155,132 154,140 162,136" fill="none" stroke={CART} strokeWidth="1.1"/>
      {/* Ribs R */}
      <path d="M198,95  C210,96  224,100 230,112" fill="none" stroke={BONE} strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M198,105 C210,108 225,114 230,126" fill="none" stroke={BONE} strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M198,115 C210,118 225,124 228,136" fill="none" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M198,125 C210,128 224,132 226,142" fill="none" stroke={BONE} strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M230,112 C234,118 235,126 230,126" fill="none" stroke={CART} strokeWidth="1.2"/>
      <path d="M230,126 C235,132 236,140 228,136" fill="none" stroke={CART} strokeWidth="1.1"/>
      {/* Lumbar vertebrae */}
      <rect x="190" y="152" width="10" height="8" rx="1.5" fill={BONE} stroke={BSTR} strokeWidth="0.6"/>
      <rect x="190" y="162" width="10" height="8" rx="1.5" fill={BONE} stroke={BSTR} strokeWidth="0.6"/>
      <rect x="190" y="172" width="10" height="8" rx="1.5" fill={BONE} stroke={BSTR} strokeWidth="0.6"/>
      <rect x="190" y="182" width="10" height="8" rx="1.5" fill={BONE} stroke={BSTR} strokeWidth="0.6"/>
      <rect x="190" y="192" width="10" height="8" rx="1.5" fill={BONE} stroke={BSTR} strokeWidth="0.6"/>
      {/* Pelvis */}
      <path d="M158,202 C152,212 152,228 160,240 C168,250 180,256 195,256 C210,256 222,250 230,240 C238,228 238,212 232,202 C224,196 210,192 195,192 C180,192 166,196 158,202Z" fill="none" stroke={BONE} strokeWidth="1.5"/>
      <circle cx="163" cy="242" r="8" fill="none" stroke={BONE} strokeWidth="1.2"/>
      <circle cx="227" cy="242" r="8" fill="none" stroke={BONE} strokeWidth="1.2"/>
      {/* Femurs */}
      <line x1="168" y1="250" x2="162" y2="342" stroke={BONE} strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="222" y1="250" x2="228" y2="342" stroke={BONE} strokeWidth="2.5" strokeLinecap="round"/>
      {/* Patellae */}
      <ellipse cx="162" cy="344" rx="10" ry="8" fill={BONE} stroke={BSTR} strokeWidth="0.8"/>
      <ellipse cx="228" cy="344" rx="10" ry="8" fill={BONE} stroke={BSTR} strokeWidth="0.8"/>
      {/* Tibia / Fibula L */}
      <line x1="160" y1="354" x2="158" y2="444" stroke={BONE} strokeWidth="2.2" strokeLinecap="round"/>
      <line x1="168" y1="356" x2="166" y2="440" stroke={BONE} strokeWidth="1.2" strokeLinecap="round"/>
      {/* Tibia / Fibula R */}
      <line x1="230" y1="354" x2="232" y2="444" stroke={BONE} strokeWidth="2.2" strokeLinecap="round"/>
      <line x1="222" y1="356" x2="224" y2="440" stroke={BONE} strokeWidth="1.2" strokeLinecap="round"/>
      {/* Humerus L */}
      <line x1="152" y1="98" x2="118" y2="182" stroke={BONE} strokeWidth="2.2" strokeLinecap="round"/>
      {/* Radius / Ulna L */}
      <line x1="114" y1="188" x2="104" y2="286" stroke={BONE} strokeWidth="1.8" strokeLinecap="round"/>
      <line x1="120" y1="188" x2="112" y2="284" stroke={BONE} strokeWidth="1.2" strokeLinecap="round"/>
      {/* Humerus R */}
      <line x1="238" y1="98" x2="272" y2="182" stroke={BONE} strokeWidth="2.2" strokeLinecap="round"/>
      {/* Radius / Ulna R */}
      <line x1="276" y1="188" x2="286" y2="286" stroke={BONE} strokeWidth="1.8" strokeLinecap="round"/>
      <line x1="270" y1="188" x2="278" y2="284" stroke={BONE} strokeWidth="1.2" strokeLinecap="round"/>

      {/* ── Joints / Cartilage ── */}
      <circle cx="150" cy="94" r="9" fill={CART} opacity="0.5"/>
      <circle cx="240" cy="94" r="9" fill={CART} opacity="0.5"/>
      <ellipse cx="116" cy="185" rx="9" ry="7" fill={CART} opacity="0.5"/>
      <ellipse cx="274" cy="185" rx="9" ry="7" fill={CART} opacity="0.5"/>
      <rect x="100" y="285" width="16" height="10" rx="3" fill={CART} opacity="0.5"/>
      <rect x="274" y="285" width="16" height="10" rx="3" fill={CART} opacity="0.5"/>
      <ellipse cx="162" cy="350" rx="10" ry="5" fill={CART} opacity="0.4"/>
      <ellipse cx="228" cy="350" rx="10" ry="5" fill={CART} opacity="0.4"/>
      <ellipse cx="158" cy="444" rx="10" ry="6" fill={CART} opacity="0.4"/>
      <ellipse cx="232" cy="444" rx="10" ry="6" fill={CART} opacity="0.4"/>
      {/* Patellar tendons */}
      <line x1="162" y1="352" x2="162" y2="366" stroke={TEND} strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="228" y1="352" x2="228" y2="366" stroke={TEND} strokeWidth="2.5" strokeLinecap="round"/>

      {/* ── Muscles ── */}
      {/* SCM */}
      <line x1="189" y1="66" x2="182" y2="88" stroke={MUS} strokeWidth="1.5"/>
      <line x1="201" y1="66" x2="208" y2="88" stroke={MUS} strokeWidth="1.5"/>
      {/* Pec major L */}
      <path d="M186,92 C175,96 163,102 160,114 C158,126 166,134 180,134 C188,134 192,128 195,124" fill={MUS} opacity="0.7" stroke={MSTR} strokeWidth="0.8"/>
      <line x1="186" y1="94"  x2="162" y2="112" stroke={MSTR} strokeWidth="0.7" opacity="0.6"/>
      <line x1="186" y1="100" x2="162" y2="118" stroke={MSTR} strokeWidth="0.7" opacity="0.6"/>
      <line x1="186" y1="108" x2="162" y2="126" stroke={MSTR} strokeWidth="0.7" opacity="0.6"/>
      {/* Pec major R */}
      <path d="M204,92 C215,96 227,102 230,114 C232,126 224,134 210,134 C202,134 198,128 195,124" fill={MUS} opacity="0.7" stroke={MSTR} strokeWidth="0.8"/>
      <line x1="204" y1="94"  x2="228" y2="112" stroke={MSTR} strokeWidth="0.7" opacity="0.6"/>
      <line x1="204" y1="100" x2="228" y2="118" stroke={MSTR} strokeWidth="0.7" opacity="0.6"/>
      <line x1="204" y1="108" x2="228" y2="126" stroke={MSTR} strokeWidth="0.7" opacity="0.6"/>
      {/* Deltoid L */}
      <path d="M148,88 C140,96 134,112 136,126 C140,130 148,126 152,116 L154,98Z" fill={MUS} opacity="0.65" stroke={MSTR} strokeWidth="0.8"/>
      {/* Deltoid R */}
      <path d="M242,88 C250,96 256,112 254,126 C250,130 242,126 238,116 L236,98Z" fill={MUS} opacity="0.65" stroke={MSTR} strokeWidth="0.8"/>
      {/* Biceps L */}
      <path d="M140,120 C134,136 130,158 132,172 C136,178 142,176 146,168 C144,154 142,138 142,122Z" fill={MUS} opacity="0.6" stroke={MSTR} strokeWidth="0.8"/>
      <line x1="137" y1="174" x2="116" y2="186" stroke={TEND} strokeWidth="1.5"/>
      {/* Biceps R */}
      <path d="M250,120 C256,136 260,158 258,172 C254,178 248,176 244,168 C246,154 248,138 248,122Z" fill={MUS} opacity="0.6" stroke={MSTR} strokeWidth="0.8"/>
      <line x1="253" y1="174" x2="274" y2="186" stroke={TEND} strokeWidth="1.5"/>
      {/* Abs 6-pack */}
      <rect x="183" y="142" width="11" height="10" rx="2.5" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      <rect x="196" y="142" width="11" height="10" rx="2.5" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      <rect x="183" y="154" width="11" height="10" rx="2.5" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      <rect x="196" y="154" width="11" height="10" rx="2.5" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      <rect x="184" y="166" width="10" height="9"  rx="2.5" fill={MUS} opacity="0.45" stroke={MSTR} strokeWidth="0.7"/>
      <rect x="196" y="166" width="10" height="9"  rx="2.5" fill={MUS} opacity="0.45" stroke={MSTR} strokeWidth="0.7"/>
      {/* Obliques */}
      <path d="M158,142 C154,158 152,178 154,196" stroke={MUS} strokeWidth="2.5" fill="none" opacity="0.6"/>
      <path d="M232,142 C236,158 238,178 236,196" stroke={MUS} strokeWidth="2.5" fill="none" opacity="0.6"/>
      {/* Quads L */}
      <path d="M158,270 C152,290 150,318 152,344" stroke={MUS} strokeWidth="8" fill="none" opacity="0.5" strokeLinecap="round"/>
      <line x1="156" y1="280" x2="154" y2="310" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      <line x1="162" y1="276" x2="160" y2="306" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      {/* Quads R */}
      <path d="M232,270 C238,290 240,318 238,344" stroke={MUS} strokeWidth="8" fill="none" opacity="0.5" strokeLinecap="round"/>
      <line x1="234" y1="280" x2="236" y2="310" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      <line x1="228" y1="276" x2="230" y2="306" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      {/* Tibialis anterior L */}
      <path d="M156,366 C154,386 154,412 156,436" stroke={MUS} strokeWidth="5" fill="none" opacity="0.5" strokeLinecap="round"/>
      {/* Tibialis anterior R */}
      <path d="M234,366 C236,386 236,412 234,436" stroke={MUS} strokeWidth="5" fill="none" opacity="0.5" strokeLinecap="round"/>

      {/* ── Organs (clipped to torso) ── */}
      <g clipPath="url(#romTcF)">
        <ellipse cx="172" cy="118" rx="18" ry="28" fill="rgba(220,140,150,0.45)"/>
        <ellipse cx="218" cy="118" rx="18" ry="28" fill="rgba(220,140,150,0.45)"/>
        <path d="M186,130 C184,124 178,122 176,128 C174,134 180,142 186,148 C192,142 198,134 196,128 C194,122 188,124 186,130Z" fill="rgba(200,40,60,0.7)"/>
        <path d="M195,170 C202,168 218,170 222,180 C224,190 218,196 210,196 C200,196 195,188 195,180Z" fill="rgba(148,60,28,0.55)"/>
        <ellipse cx="180" cy="182" rx="16" ry="12" fill="rgba(220,180,100,0.4)"/>
      </g>

      {/* ── Tendons ── */}
      <line x1="155" y1="268" x2="156" y2="350" stroke={TEND} strokeWidth="1.5" opacity="0.6"/>
      <line x1="235" y1="268" x2="234" y2="350" stroke={TEND} strokeWidth="1.5" opacity="0.6"/>
      <line x1="162" y1="440" x2="162" y2="458" stroke={TEND} strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="228" y1="440" x2="228" y2="458" stroke={TEND} strokeWidth="2.5" strokeLinecap="round"/>

      {/* ── Nerve pathways ── */}
      <g filter="url(#romNgF)">
        <path d="M188,76 C178,82 162,92 148,104" stroke={NERV} strokeWidth="1.2" fill="none" strokeDasharray="4,3"/>
        <path d="M202,76 C212,82 228,92 242,104" stroke={NERV} strokeWidth="1.2" fill="none" strokeDasharray="4,3"/>
        <path d="M136,170 C126,190 116,220 108,270" stroke={NERV} strokeWidth="1" fill="none" strokeDasharray="3,4"/>
        <path d="M254,170 C264,190 274,220 282,270" stroke={NERV} strokeWidth="1" fill="none" strokeDasharray="3,4"/>
        <path d="M175,250 C168,280 162,320 162,350" stroke={NERV} strokeWidth="1.2" fill="none" strokeDasharray="4,3"/>
        <path d="M215,250 C222,280 228,320 228,350" stroke={NERV} strokeWidth="1.2" fill="none" strokeDasharray="4,3"/>
        <path d="M162,360 C160,390 158,420 160,444" stroke={NERV} strokeWidth="1" fill="none" strokeDasharray="3,4"/>
        <path d="M228,360 C230,390 232,420 230,444" stroke={NERV} strokeWidth="1" fill="none" strokeDasharray="3,4"/>
      </g>
    </>
  );
}

function RomBackBodySVG() {
  const SKIN = "rgba(230,185,148,0.12)";
  const SSTR = "rgba(205,162,118,0.38)";
  const BONE = "rgba(238,222,182,0.90)";
  const BSTR = "rgba(205,188,140,0.88)";
  const MUS  = "rgba(186,52,32,0.68)";
  const MSTR = "rgba(145,28,16,0.60)";
  const TEND = "rgba(218,178,60,0.82)";
  const CART = "rgba(110,188,225,0.70)";
  const NERV = "rgba(252,214,28,0.80)";
  return (
    <>
      <defs>
        <radialGradient id="romBgB" cx="50%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#18263a"/>
          <stop offset="100%" stopColor="#0a1018"/>
        </radialGradient>
        <clipPath id="romTcB"><rect x="152" y="155" width="86" height="60"/></clipPath>
        <filter id="romNgB" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="1.8" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      {/* Background */}
      <rect x="0" y="0" width="390" height="490" fill="url(#romBgB)"/>

      {/* ── Skin silhouette ── */}
      <ellipse cx="195" cy="34" rx="26" ry="30" fill={SKIN} stroke={SSTR} strokeWidth="1.2"/>
      <rect x="185" y="62" width="20" height="24" rx="4" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M152,84 C145,100 140,160 142,198 C144,220 160,240 195,242 C230,240 246,220 248,198 C250,160 245,100 238,84 C228,78 210,74 195,74 C180,74 162,78 152,84Z" fill={SKIN} stroke={SSTR} strokeWidth="1"/>
      <path d="M152,90 C138,108 122,148 112,192 C108,210 110,242 120,242 C130,242 132,210 134,192 L148,144Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M238,90 C252,108 268,148 278,192 C282,210 280,242 270,242 C260,242 258,210 256,192 L242,144Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M108,242 C102,256 100,272 102,290 L112,294 L122,244Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M282,242 C288,256 290,272 288,290 L278,294 L268,244Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M155,200 C150,222 150,248 158,268 L192,270 L198,270 L232,268 C240,248 240,222 235,200Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M158,268 C152,290 150,324 152,352 C154,368 162,374 174,370 L180,312 L192,268Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M154,356 C150,382 150,416 152,444 C154,456 164,462 176,458 L180,426 L180,360Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M232,268 C238,290 240,324 238,352 C236,368 228,374 216,370 L210,312 L198,268Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M236,356 C240,382 240,416 238,444 C236,456 226,462 214,458 L210,426 L210,360Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>

      {/* ── Skeleton ── */}
      {/* Skull (back) */}
      <ellipse cx="195" cy="30" rx="22" ry="25" fill="none" stroke={BONE} strokeWidth="1.2"/>
      {/* Thoracic spine */}
      {[90,100,110,120,130,140,150,160,168,176,184,192].map((y, i) => (
        <g key={i}>
          <rect x="191" y={y} width="8" height="8" rx="1" fill={BONE} stroke={BSTR} strokeWidth="0.5"/>
          <rect x="192" y={y+8} width="6" height="3" rx="0.5" fill={BSTR} strokeWidth="0"/>
          <line x1="188" y1={y+4} x2="180" y2={y+4} stroke={BSTR} strokeWidth="1"/>
          <line x1="202" y1={y+4} x2="210" y2={y+4} stroke={BSTR} strokeWidth="1"/>
        </g>
      ))}
      {/* Sacrum */}
      <path d="M185,202 L205,202 L208,228 L195,234 L182,228Z" fill={BONE} stroke={BSTR} strokeWidth="0.8"/>
      {/* Coccyx */}
      <path d="M191,234 L199,234 L197,248 L195,250 L193,248Z" fill={BONE} stroke={BSTR} strokeWidth="0.7"/>
      {/* Scapulae */}
      <path d="M150,92 C144,110 143,130 148,148 C156,156 168,154 172,144 C174,134 174,112 168,98 C164,92 156,88 150,92Z" fill="none" stroke={BONE} strokeWidth="1.5"/>
      <path d="M150,100 C158,104 166,112 170,122" stroke={BONE} strokeWidth="1.2" fill="none"/>
      <ellipse cx="150" cy="94" rx="6" ry="5" fill={BONE} opacity="0.5"/>
      <path d="M240,92 C246,110 247,130 242,148 C234,156 222,154 218,144 C216,134 216,112 222,98 C226,92 234,88 240,92Z" fill="none" stroke={BONE} strokeWidth="1.5"/>
      <path d="M240,100 C232,104 224,112 220,122" stroke={BONE} strokeWidth="1.2" fill="none"/>
      <ellipse cx="240" cy="94" rx="6" ry="5" fill={BONE} opacity="0.5"/>
      {/* Pelvis back / iliac crests */}
      <path d="M155,202 C148,208 145,220 148,234 C155,244 170,250 195,252 C220,250 235,244 242,234 C245,220 242,208 235,202" fill="none" stroke={BONE} strokeWidth="1.5"/>
      <path d="M148,206 C143,202 140,196 145,190 C152,184 170,182 195,182 C220,182 238,184 245,190 C250,196 247,202 242,206" fill="none" stroke={BONE} strokeWidth="1.2"/>
      {/* Long bones */}
      <line x1="152" y1="98" x2="118" y2="182" stroke={BONE} strokeWidth="2.2" strokeLinecap="round"/>
      <line x1="238" y1="98" x2="272" y2="182" stroke={BONE} strokeWidth="2.2" strokeLinecap="round"/>
      <line x1="114" y1="188" x2="104" y2="286" stroke={BONE} strokeWidth="1.8" strokeLinecap="round"/>
      <line x1="120" y1="188" x2="112" y2="284" stroke={BONE} strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="276" y1="188" x2="286" y2="286" stroke={BONE} strokeWidth="1.8" strokeLinecap="round"/>
      <line x1="270" y1="188" x2="278" y2="284" stroke={BONE} strokeWidth="1.2" strokeLinecap="round"/>
      {/* Femurs back */}
      <line x1="170" y1="252" x2="164" y2="342" stroke={BONE} strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="220" y1="252" x2="226" y2="342" stroke={BONE} strokeWidth="2.5" strokeLinecap="round"/>
      {/* Tibia / Fibula */}
      <line x1="162" y1="354" x2="160" y2="444" stroke={BONE} strokeWidth="2.2" strokeLinecap="round"/>
      <line x1="170" y1="356" x2="168" y2="440" stroke={BONE} strokeWidth="1.2" strokeLinecap="round"/>
      <line x1="228" y1="354" x2="230" y2="444" stroke={BONE} strokeWidth="2.2" strokeLinecap="round"/>
      <line x1="220" y1="356" x2="222" y2="440" stroke={BONE} strokeWidth="1.2" strokeLinecap="round"/>

      {/* ── Joints ── */}
      <circle cx="150" cy="94" r="9" fill={CART} opacity="0.45"/>
      <circle cx="240" cy="94" r="9" fill={CART} opacity="0.45"/>
      <ellipse cx="116" cy="185" rx="9" ry="7" fill={CART} opacity="0.45"/>
      <ellipse cx="274" cy="185" rx="9" ry="7" fill={CART} opacity="0.45"/>
      <rect x="100" y="285" width="16" height="10" rx="3" fill={CART} opacity="0.45"/>
      <rect x="274" y="285" width="16" height="10" rx="3" fill={CART} opacity="0.45"/>
      <ellipse cx="164" cy="350" rx="11" ry="7" fill={CART} opacity="0.4"/>
      <ellipse cx="226" cy="350" rx="11" ry="7" fill={CART} opacity="0.4"/>
      {/* Achilles tendons */}
      <line x1="163" y1="440" x2="163" y2="460" stroke={TEND} strokeWidth="3" strokeLinecap="round"/>
      <line x1="227" y1="440" x2="227" y2="460" stroke={TEND} strokeWidth="3" strokeLinecap="round"/>

      {/* ── Muscles ── */}
      {/* Trapezius upper */}
      <path d="M186,82 C180,88 162,96 155,104 C162,108 176,112 186,110 L195,106 L204,110 C214,112 228,108 235,104 C228,96 210,88 204,82Z" fill={MUS} opacity="0.6" stroke={MSTR} strokeWidth="0.8"/>
      {/* Trapezius mid */}
      <path d="M155,104 C150,116 149,132 152,146 C160,152 178,152 186,148 L186,110 L195,106 L204,110 L204,148 C212,152 230,152 238,146 C241,132 240,116 235,104" fill={MUS} opacity="0.45" stroke={MSTR} strokeWidth="0.7"/>
      {/* Rhomboid fiber lines */}
      <line x1="186" y1="112" x2="164" y2="126" stroke={MSTR} strokeWidth="0.8" opacity="0.5"/>
      <line x1="186" y1="120" x2="160" y2="134" stroke={MSTR} strokeWidth="0.8" opacity="0.5"/>
      <line x1="204" y1="112" x2="226" y2="126" stroke={MSTR} strokeWidth="0.8" opacity="0.5"/>
      <line x1="204" y1="120" x2="230" y2="134" stroke={MSTR} strokeWidth="0.8" opacity="0.5"/>
      {/* Infraspinatus L */}
      <path d="M150,102 C144,114 144,130 148,144 C154,150 164,150 168,140 C170,130 168,114 164,104Z" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      {/* Infraspinatus R */}
      <path d="M240,102 C246,114 246,130 242,144 C236,150 226,150 222,140 C220,130 222,114 226,104Z" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      {/* Latissimus L */}
      <path d="M155,108 C148,128 144,158 148,190 C155,198 170,200 180,190 L178,144 C172,132 163,118 155,108Z" fill={MUS} opacity="0.5" stroke={MSTR} strokeWidth="0.8"/>
      {/* Latissimus R */}
      <path d="M235,108 C242,128 246,158 242,190 C235,198 220,200 210,190 L212,144 C218,132 227,118 235,108Z" fill={MUS} opacity="0.5" stroke={MSTR} strokeWidth="0.8"/>
      {/* Erector spinae */}
      <rect x="184" y="150" width="7" height="50" rx="3" fill={MUS} opacity="0.45" stroke={MSTR} strokeWidth="0.7"/>
      <rect x="199" y="150" width="7" height="50" rx="3" fill={MUS} opacity="0.45" stroke={MSTR} strokeWidth="0.7"/>
      <line x1="186" y1="158" x2="186" y2="195" stroke={MSTR} strokeWidth="0.8" opacity="0.6"/>
      <line x1="204" y1="158" x2="204" y2="195" stroke={MSTR} strokeWidth="0.8" opacity="0.6"/>
      {/* Deltoid L back */}
      <path d="M148,88 C140,96 134,112 136,126 C140,130 148,126 152,116 L154,98Z" fill={MUS} opacity="0.6" stroke={MSTR} strokeWidth="0.8"/>
      {/* Deltoid R back */}
      <path d="M242,88 C250,96 256,112 254,126 C250,130 242,126 238,116 L236,98Z" fill={MUS} opacity="0.6" stroke={MSTR} strokeWidth="0.8"/>
      {/* Triceps L */}
      <path d="M144,120 C136,138 132,160 134,176 C138,182 144,180 148,172 C146,156 146,138 148,122Z" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      {/* Triceps R */}
      <path d="M246,120 C254,138 258,160 256,176 C252,182 246,180 242,172 C244,156 244,138 242,122Z" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      {/* Gluteus maximus L */}
      <path d="M158,202 C150,216 148,236 154,254 C160,262 174,264 184,258 L188,222Z" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.8"/>
      {/* Gluteus maximus R */}
      <path d="M232,202 C240,216 242,236 236,254 C230,262 216,264 206,258 L202,222Z" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.8"/>
      {/* Hamstrings L */}
      <path d="M162,268 C155,290 153,318 155,346" stroke={MUS} strokeWidth="9" fill="none" opacity="0.45" strokeLinecap="round"/>
      <line x1="158" y1="278" x2="156" y2="310" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      <line x1="165" y1="274" x2="163" y2="306" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      {/* Hamstrings R */}
      <path d="M228,268 C235,290 237,318 235,346" stroke={MUS} strokeWidth="9" fill="none" opacity="0.45" strokeLinecap="round"/>
      <line x1="232" y1="278" x2="234" y2="310" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      <line x1="225" y1="274" x2="227" y2="306" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      {/* Gastrocnemius L */}
      <path d="M158,366 C153,382 153,402 157,420 C161,428 168,428 170,420 C167,404 165,386 163,368Z" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.8"/>
      {/* Soleus L */}
      <path d="M163,420 C161,430 161,440 163,452" stroke={MUS} strokeWidth="4" fill="none" opacity="0.4" strokeLinecap="round"/>
      {/* Gastrocnemius R */}
      <path d="M232,366 C237,382 237,402 233,420 C229,428 222,428 220,420 C223,404 225,386 227,368Z" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.8"/>
      {/* Soleus R */}
      <path d="M227,420 C229,430 229,440 227,452" stroke={MUS} strokeWidth="4" fill="none" opacity="0.4" strokeLinecap="round"/>

      {/* ── Organs (kidneys) ── */}
      <g clipPath="url(#romTcB)">
        <path d="M165,162 C159,162 155,168 155,176 C155,184 159,190 165,192 C170,192 174,188 175,182 C175,174 171,164 168,162Z" fill="rgba(180,60,40,0.5)"/>
        <line x1="165" y1="192" x2="165" y2="204" stroke="rgba(180,60,40,0.4)" strokeWidth="1.5"/>
        <path d="M225,162 C231,162 235,168 235,176 C235,184 231,190 225,192 C220,192 216,188 215,182 C215,174 219,164 222,162Z" fill="rgba(180,60,40,0.5)"/>
        <line x1="225" y1="192" x2="225" y2="204" stroke="rgba(180,60,40,0.4)" strokeWidth="1.5"/>
        {/* Adrenal glands */}
        <ellipse cx="166" cy="160" rx="6" ry="4" fill="rgba(220,160,60,0.55)"/>
        <ellipse cx="224" cy="160" rx="6" ry="4" fill="rgba(220,160,60,0.55)"/>
      </g>

      {/* ── IT bands ── */}
      <line x1="154" y1="268" x2="155" y2="350" stroke={TEND} strokeWidth="1.5" opacity="0.6"/>
      <line x1="236" y1="268" x2="235" y2="350" stroke={TEND} strokeWidth="1.5" opacity="0.6"/>

      {/* ── Nerves ── */}
      <g filter="url(#romNgB)">
        {/* Spinal cord */}
        <path d="M195,62 C195,100 195,140 195,200" stroke={NERV} strokeWidth="1.5" fill="none" strokeDasharray="5,4"/>
        {/* Brachial plexus */}
        <path d="M188,76 C178,82 162,92 148,104" stroke={NERV} strokeWidth="1.2" fill="none" strokeDasharray="4,3"/>
        <path d="M202,76 C212,82 228,92 242,104" stroke={NERV} strokeWidth="1.2" fill="none" strokeDasharray="4,3"/>
        {/* Sciatic nerves */}
        <path d="M176,248 C168,270 162,310 160,350" stroke={NERV} strokeWidth="1.5" fill="none" strokeDasharray="5,3"/>
        <path d="M214,248 C222,270 228,310 230,350" stroke={NERV} strokeWidth="1.5" fill="none" strokeDasharray="5,3"/>
        {/* Tibial nerves */}
        <path d="M160,360 C160,390 160,418 162,444" stroke={NERV} strokeWidth="1" fill="none" strokeDasharray="3,4"/>
        <path d="M230,360 C230,390 230,418 228,444" stroke={NERV} strokeWidth="1" fill="none" strokeDasharray="3,4"/>
      </g>
    </>
  );
}

// ── Progress history chart + table ────────────────────────────────────────────
const CHART_COLORS = ['#2196f3','#f44336','#4caf50','#ff9800','#9c27b0','#00bcd4','#795548','#607d8b'];

// MVC metric configs for charting & table
const MVC_METRICS = {
  strength:  { label: 'Str (N)',      field: 'strength',    domain: ['auto', 'auto'], unit: 'N',   fmt: v => `${v} N` },
  grade:     { label: 'Grade (0-5)',   field: 'grade',       domain: [0, 5],           unit: '',    fmt: v => `Grade ${v}` },
  endurance: { label: 'Endurance (s)', field: 'endurance',   domain: ['auto', 'auto'], unit: 's',   fmt: v => `${v}s` },
  pain:      { label: 'Pain (0-10)',   field: 'pain',        domain: [0, 10],          unit: '/10', fmt: v => `${v}/10` },
};

function PartHistoryChart({ history, partKey, side, activeTab, romParts, muscleParts, muscleKeys, chartKey, tableKey, isSectionCollapsed, toggleSection }) {
  const rp = romParts || ROM_PARTS;
  const mp = muscleParts || MUSCLE_PARTS;
  const [mvcMetric, setMvcMetric] = useState('strength');

  if (!history || history.length === 0) return null;

  let chartData, lines, partLabel, items;
  const metricCfg = MVC_METRICS[mvcMetric] || MVC_METRICS.strength;

  if (activeTab === 'rom') {
    const part = rp.find(p => p.key === partKey);
    if (!part) return null;

    items = part.movements;
    const isBilat = part.bilateral;

    chartData = history
      .filter(snap => snap.rom?.[partKey])
      .map(snap => {
        const pd = snap.rom[partKey];
        const entry = { date: new Date(snap.assessmentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) };
        items.forEach(item => {
          const mv = pd[item.key] || {};
          const d = (isBilat && side) ? (mv[side] || {}) : mv;
          if (d.active  != null) entry[`${item.key}_A`] = Number(d.active);
          if (d.passive != null) entry[`${item.key}_P`] = Number(d.passive);
        });
        return entry;
      });

    if (chartData.length === 0) return null;

    lines = items.flatMap((item, i) => [
      { key: `${item.key}_A`, label: `${item.label} (Active)`,  color: CHART_COLORS[i % CHART_COLORS.length], dash: false },
      { key: `${item.key}_P`, label: `${item.label} (Passive)`, color: CHART_COLORS[i % CHART_COLORS.length], dash: true  },
    ]);

    partLabel = `${part.label}${isBilat && side ? ` — ${side === 'left' ? 'Left' : 'Right'}` : ''}`;

  } else {
    // MVC: muscleStrength data is nested as [bpKey][muscleKey][groupKey]
    if (!muscleKeys || muscleKeys.length === 0) return null;
    const mLookup = buildMuscleLookup(mp);
    const muscles = muscleKeys.map(mk => mLookup[mk]).filter(Boolean);
    if (muscles.length === 0) return null;

    // Build flat list of muscle→group combos for charting
    items = [];
    muscles.forEach(m => {
      (m.groups || []).forEach(g => {
        items.push({
          key: `${m.key}__${g.key}`,
          muscleKey: m.key, groupKey: g.key,
          label: muscles.length === 1 && m.groups.length === 1 ? m.label
            : m.groups.length === 1 ? m.label : `${m.label} — ${g.label}`,
        });
      });
    });
    if (items.length === 0) return null;

    const bpKey = partKey; // for MVC, partKey is the body-part key (e.g. 'knee_L')
    chartData = history
      .filter(snap => snap.muscleStrength?.[bpKey])
      .map(snap => {
        const bpData = snap.muscleStrength[bpKey];
        const entry = { date: new Date(snap.assessmentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) };
        items.forEach(fi => {
          const gd = bpData?.[fi.muscleKey]?.[fi.groupKey] || {};
          const val = gd[metricCfg.field];
          if (val != null && val !== '') entry[fi.key] = Number(val);
        });
        return entry;
      });

    if (chartData.length === 0) return null;

    lines = items.map((fi, i) => ({
      key: fi.key, label: fi.label, color: CHART_COLORS[i % CHART_COLORS.length], dash: false,
    }));

    const isBilat = muscles.some(m => m.bilateral);
    partLabel = muscles.map(m => m.label).join(', ') + (isBilat && side ? ` — ${side === 'left' ? 'Left' : 'Right'}` : '');
  }

  const chartCollapsed = chartKey && isSectionCollapsed ? isSectionCollapsed(chartKey) : false;
  const tableCollapsed = tableKey && isSectionCollapsed ? isSectionCollapsed(tableKey) : true;

  return (
    <>
      {/* Sub-section 2: Progress Chart */}
      {chartData.length > 1 && (
        <>
          <div
            onClick={() => toggleSection?.(chartKey)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 16px', cursor: 'pointer', background: 'linear-gradient(90deg, #ecfdf508, #f0fdf4)', borderTop: '1px solid #e8edf5', borderBottom: '1px solid #e8edf5', userSelect: 'none' }}
          >
            <span style={{ fontSize: 9, display: 'inline-block', transition: 'transform 0.15s', transform: chartCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▼</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#15803d' }}>Progress Chart</span>
            {activeTab === 'muscle' && (
              <select
                value={mvcMetric}
                onChange={e => { e.stopPropagation(); setMvcMetric(e.target.value); }}
                onClick={e => e.stopPropagation()}
                style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, border: '1.5px solid #d1d5db', borderRadius: 6, padding: '2px 6px', background: '#f9fafb', cursor: 'pointer', outline: 'none' }}
              >
                {Object.entries(MVC_METRICS).map(([k, m]) => (
                  <option key={k} value={k}>{m.label}</option>
                ))}
              </select>
            )}
            <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>{partLabel} · {chartData.length} reading{chartData.length !== 1 ? 's' : ''}</span>
          </div>
          {!chartCollapsed && (
            <div style={{ padding: '12px 4px 0' }}>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData} margin={{ top: 4, right: 20, left: -10, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f4" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis
                    domain={activeTab === 'muscle' ? metricCfg.domain : ['auto', 'auto']}
                    tick={{ fontSize: 10 }}
                    tickFormatter={v => activeTab === 'muscle' ? (metricCfg.unit ? `${v}${metricCfg.unit}` : `${v}`) : `${v}°`}
                  />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 8 }}
                    formatter={(v, name) => [activeTab === 'muscle' ? metricCfg.fmt(v) : `${v}°`, name]}
                  />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {lines.map(l => (
                    <Line
                      key={l.key} type="monotone" dataKey={l.key}
                      name={l.label} stroke={l.color}
                      strokeDasharray={l.dash ? '5 3' : undefined}
                      strokeWidth={1.8} dot={{ r: 3 }} connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}

      {/* Sub-section 3: History Data Table (collapsed by default) */}
      <div
        onClick={() => toggleSection?.(tableKey)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 16px', cursor: 'pointer', background: 'linear-gradient(90deg, #fff7ed08, #fef3c7)', borderTop: '1px solid #e8edf5', borderBottom: tableCollapsed ? 'none' : '1px solid #e8edf5', userSelect: 'none' }}
      >
        <span style={{ fontSize: 9, display: 'inline-block', transition: 'transform 0.15s', transform: tableCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▼</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#c2410c' }}>History Data</span>
        {activeTab === 'muscle' && (
          <select
            value={mvcMetric}
            onChange={e => { e.stopPropagation(); setMvcMetric(e.target.value); }}
            onClick={e => e.stopPropagation()}
            style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, border: '1.5px solid #d1d5db', borderRadius: 6, padding: '2px 6px', background: '#f9fafb', cursor: 'pointer', outline: 'none' }}
          >
            {Object.entries(MVC_METRICS).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </select>
        )}
        <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>{chartData.length} record{chartData.length !== 1 ? 's' : ''}</span>
      </div>
      {!tableCollapsed && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: '#f7f9fc' }}>
                <th style={{ ...thS, fontSize: 11 }}>Date</th>
                {activeTab === 'rom'
                  ? items.map(item => (
                      <React.Fragment key={item.key}>
                        <th style={{ ...thS, fontSize: 10, textAlign: 'center', color: '#1d4ed8' }}>{item.label}<br />Active</th>
                        <th style={{ ...thS, fontSize: 10, textAlign: 'center', color: '#6d28d9' }}>{item.label}<br />Passive</th>
                      </React.Fragment>
                    ))
                  : items.map(item => (
                      <th key={item.key} style={{ ...thS, fontSize: 11, textAlign: 'center' }}>{item.label}<br /><span style={{ fontSize: 9, color: '#6b7280' }}>{metricCfg.label}</span></th>
                    ))
                }
              </tr>
            </thead>
            <tbody>
              {[...chartData].reverse().map((row, ri) => (
                <tr key={ri} style={{ background: ri % 2 ? '#fafbfd' : '#fff' }}>
                  <td style={{ ...tdS, fontSize: 11, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{row.date}</td>
                  {activeTab === 'rom'
                    ? items.flatMap(item => [
                        <td key={`${item.key}_A`} style={{ ...tdS, textAlign: 'center', fontSize: 11 }}>
                          {row[`${item.key}_A`] != null ? `${row[`${item.key}_A`]}°` : '—'}
                        </td>,
                        <td key={`${item.key}_P`} style={{ ...tdS, textAlign: 'center', fontSize: 11 }}>
                          {row[`${item.key}_P`] != null ? `${row[`${item.key}_P`]}°` : '—'}
                        </td>,
                      ])
                    : items.map(item => (
                        <td key={item.key} style={{ ...tdS, textAlign: 'center', fontSize: 11 }}>
                          {row[item.key] != null ? metricCfg.fmt(row[item.key]) : '—'}
                        </td>
                      ))
                  }
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────
function NumInput({ val, onChange, editable }) {
  if (!editable) {
    return <span style={{ fontWeight: 600, color: '#1a1d23' }}>{val != null ? `${val}°` : '—'}</span>;
  }
  return (
    <input
      type="number" min="0" max="360"
      value={val ?? ''}
      onChange={e => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      style={{
        width: 58, border: '1.5px solid #d0d8e8', borderRadius: 7,
        padding: '4px', fontSize: 13, textAlign: 'center',
        background: '#fff', outline: 'none', fontWeight: 500,
      }}
    />
  );
}

function PainCheck({ val, onChange, editable }) {
  return (
    <input
      type="checkbox" checked={!!val}
      onChange={e => editable && onChange(e.target.checked)}
      readOnly={!editable}
      style={{ width: 16, height: 16, cursor: editable ? 'pointer' : 'default', accentColor: '#f44336' }}
    />
  );
}

function GradeSelect({ val, onChange, editable }) {
  const v = val != null ? String(val) : '';
  if (!editable) {
    const opt = MVC_OPTIONS.find(o => o.value === v);
    return (
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: 20,
        fontSize: 12, fontWeight: 600,
        background: MVC_BG[v] || '#f5f5f5', color: MVC_COLOR[v] || '#888',
      }}>{opt?.label || '—'}</span>
    );
  }
  return (
    <select
      value={v}
      onChange={e => onChange(e.target.value)}
      style={{
        border: '1.5px solid #d0d8e8', borderRadius: 7, padding: '4px 8px',
        fontSize: 13, background: MVC_BG[v] || '#fff', color: MVC_COLOR[v] || '#333',
        fontWeight: 500, cursor: 'pointer', outline: 'none',
      }}
    >
      {MVC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function RomSection({ part, romData, onChange, editable, color }) {
  const pd = romData || {};
  return (
    <div style={{ marginBottom: 24, borderRadius: 14, border: `1.5px solid ${color}30`, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,.05)' }}>
      <div style={{
        background: `linear-gradient(135deg, ${color}15, ${color}05)`,
        borderBottom: `2px solid ${color}25`,
        padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{ fontSize: 20 }}>{part.icon}</span>
        <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1d23' }}>{part.label}</span>
        {part.bilateral && (
          <span style={{ fontSize: 11, background: color, color: '#fff', borderRadius: 12, padding: '2px 9px', fontWeight: 600, marginLeft: 4 }}>BILATERAL</span>
        )}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f7f9fc' }}>
              <th style={{ ...thS, minWidth: 160 }}>Movement</th>
              <th style={{ ...thS, textAlign: 'center', color: '#9ca3af' }}>Normal</th>
              {part.bilateral ? (
                <>
                  <th style={{ ...thS, background: '#dbeafe', color: '#1d4ed8', textAlign: 'center' }}>L Active°</th>
                  <th style={{ ...thS, background: '#dbeafe', color: '#1d4ed8', textAlign: 'center' }}>L Passive°</th>
                  <th style={{ ...thS, background: '#dbeafe', color: '#1d4ed8', textAlign: 'center' }}>L Pain</th>
                  <th style={{ ...thS, background: '#fecdd3', color: '#b91c1c', textAlign: 'center' }}>R Active°</th>
                  <th style={{ ...thS, background: '#fecdd3', color: '#b91c1c', textAlign: 'center' }}>R Passive°</th>
                  <th style={{ ...thS, background: '#fecdd3', color: '#b91c1c', textAlign: 'center' }}>R Pain</th>
                </>
              ) : (
                <>
                  <th style={{ ...thS, textAlign: 'center' }}>Active°</th>
                  <th style={{ ...thS, textAlign: 'center' }}>Passive°</th>
                  <th style={{ ...thS, textAlign: 'center' }}>Pain</th>
                </>
              )}
              <th style={{ ...thS, minWidth: 140 }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {part.movements.map((move, mi) => {
              const mv = pd[move.key] || {};
              const lv = mv.left  || {};
              const rv = mv.right || {};
              const upd = (...path) => val => onChange([part.key, move.key, ...path], val);
              return (
                <tr key={move.key} style={{ background: mi % 2 ? '#fafbfd' : '#fff' }}>
                  <td style={{ ...tdS, fontWeight: 500, color: '#374151' }}>{move.label}</td>
                  <td style={{ ...tdS, textAlign: 'center', color: '#9ca3af', fontWeight: 600 }}>{move.normal}°</td>
                  {part.bilateral ? (
                    <>
                      <td style={{ ...tdS, background: '#eff6ff', textAlign: 'center' }}>
                        <NumInput val={lv.active}  onChange={upd('left', 'active')}  editable={editable} />
                      </td>
                      <td style={{ ...tdS, background: '#eff6ff', textAlign: 'center' }}>
                        <NumInput val={lv.passive} onChange={upd('left', 'passive')} editable={editable} />
                      </td>
                      <td style={{ ...tdS, background: '#eff6ff', textAlign: 'center' }}>
                        <PainCheck val={lv.pain} onChange={upd('left', 'pain')} editable={editable} />
                      </td>
                      <td style={{ ...tdS, background: '#fff1f2', textAlign: 'center' }}>
                        <NumInput val={rv.active}  onChange={upd('right', 'active')}  editable={editable} />
                      </td>
                      <td style={{ ...tdS, background: '#fff1f2', textAlign: 'center' }}>
                        <NumInput val={rv.passive} onChange={upd('right', 'passive')} editable={editable} />
                      </td>
                      <td style={{ ...tdS, background: '#fff1f2', textAlign: 'center' }}>
                        <PainCheck val={rv.pain} onChange={upd('right', 'pain')} editable={editable} />
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ ...tdS, textAlign: 'center' }}>
                        <NumInput val={mv.active}  onChange={upd('active')}  editable={editable} />
                      </td>
                      <td style={{ ...tdS, textAlign: 'center' }}>
                        <NumInput val={mv.passive} onChange={upd('passive')} editable={editable} />
                      </td>
                      <td style={{ ...tdS, textAlign: 'center' }}>
                        <PainCheck val={mv.pain} onChange={upd('pain')} editable={editable} />
                      </td>
                    </>
                  )}
                  <td style={tdS}>
                    {editable ? (
                      <input
                        type="text" value={mv.notes || ''}
                        onChange={e => upd('notes')(e.target.value)}
                        placeholder="Notes…"
                        style={{
                          width: '100%', minWidth: 110, border: '1.5px solid #e2e8f0',
                          borderRadius: 6, padding: '3px 8px', fontSize: 12,
                          background: '#f8fafc', outline: 'none',
                        }}
                      />
                    ) : (
                      <span style={{ color: '#6b7280', fontSize: 12 }}>{mv.notes || ''}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MuscleSection({ part, muscleData, onChange, editable, color }) {
  const pd = muscleData || {};
  return (
    <div style={{ marginBottom: 24, borderRadius: 14, border: `1.5px solid ${color}30`, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,.05)' }}>
      <div style={{
        background: `linear-gradient(135deg, ${color}15, ${color}05)`,
        borderBottom: `2px solid ${color}25`,
        padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{ fontSize: 20 }}>{part.icon}</span>
        <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1d23' }}>{part.label}</span>
        {part.bilateral && (
          <span style={{ fontSize: 11, background: color, color: '#fff', borderRadius: 12, padding: '2px 9px', fontWeight: 600, marginLeft: 4 }}>BILATERAL</span>
        )}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f7f9fc' }}>
              <th style={{ ...thS, minWidth: 250 }}>Muscle Group</th>
              {part.bilateral ? (
                <>
                  <th style={{ ...thS, background: '#dbeafe', color: '#1d4ed8', textAlign: 'center', minWidth: 140 }}>Left Grade</th>
                  <th style={{ ...thS, background: '#fecdd3', color: '#b91c1c', textAlign: 'center', minWidth: 140 }}>Right Grade</th>
                </>
              ) : (
                <th style={{ ...thS, textAlign: 'center', minWidth: 160 }}>Grade (MVC 0–5)</th>
              )}
              <th style={{ ...thS, minWidth: 140 }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {part.groups.map((grp, gi) => {
              const gd = pd[grp.key] || {};
              const upd = (...path) => val => onChange([part.key, grp.key, ...path], val);
              return (
                <tr key={grp.key} style={{ background: gi % 2 ? '#fafbfd' : '#fff' }}>
                  <td style={{ ...tdS, fontWeight: 500, color: '#374151' }}>{grp.label}</td>
                  {part.bilateral ? (
                    <>
                      <td style={{ ...tdS, background: '#eff6ff', textAlign: 'center' }}>
                        <GradeSelect val={gd.left?.grade} onChange={upd('left', 'grade')} editable={editable} />
                      </td>
                      <td style={{ ...tdS, background: '#fff1f2', textAlign: 'center' }}>
                        <GradeSelect val={gd.right?.grade} onChange={upd('right', 'grade')} editable={editable} />
                      </td>
                    </>
                  ) : (
                    <td style={{ ...tdS, textAlign: 'center' }}>
                      <GradeSelect val={gd.grade} onChange={upd('grade')} editable={editable} />
                    </td>
                  )}
                  <td style={tdS}>
                    {editable ? (
                      <input
                        type="text" value={gd.notes || ''}
                        onChange={e => upd('notes')(e.target.value)}
                        placeholder="Notes…"
                        style={{
                          width: '100%', minWidth: 110, border: '1.5px solid #e2e8f0',
                          borderRadius: 6, padding: '3px 8px', fontSize: 12,
                          background: '#f8fafc', outline: 'none',
                        }}
                      />
                    ) : (
                      <span style={{ color: '#6b7280', fontSize: 12 }}>{gd.notes || ''}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Named export: inline panel (embed directly in a page) ─────────────────────
// Props:
//   patientCard   – required
//   patientName   – optional display name
//   activeTab     – 'rom' | 'muscle'  (controlled from outside, e.g. PatientDashboard tabs)
//   showTabBar    – boolean (default true). Pass false when the parent provides its own tabs.
//   onClose       – optional. If provided, renders the ✕ and Cancel buttons.
export function RomAssessmentPanel({
  patientCard,
  patientName,
  activeTab: propTab,
  showTabBar = true,
  onClose,
}) {
  const { auth } = useAuth();
  const { dialogNode, showAlert, showConfirm } = useDialog();

  const [activeTab,   setActiveTab]   = useState(propTab || 'rom');
  const [data,        setData]        = useState({ rom: {}, muscleStrength: {} });
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [history,     setHistory]     = useState([]);

  // Snapshot of last-saved data for dirty tracking
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  // BPConfig-derived part lists (replaces static ROM_PARTS / MUSCLE_PARTS for left menu)
  const [bpRomParts, setBpRomParts] = useState([]); // [{ key(bpKey), partKey(clinical), side, label, icon }]
  const [bpMvcParts, setBpMvcParts] = useState([]);
  const [clinicalRomParts, setClinicalRomParts] = useState(ROM_PARTS);       // from ClinicalConfig API
  const [clinicalMuscleParts, setClinicalMuscleParts] = useState(MUSCLE_PARTS); // from ClinicalConfig API

  // Multi-select body parts: Set of bpKeys like "knee_L", "cervical"
  const [selectedParts, setSelectedParts] = useState(new Set());

  // Collapse state for sub-sections: key → boolean (true = collapsed)
  // Keys: `${itemKey}_data`, `${itemKey}_chart`, `${itemKey}_table`
  // _table defaults to collapsed; others default to open
  const [sectionState, setSectionState] = useState({});
  const isSectionCollapsed = useCallback((key) => {
    if (sectionState[key] !== undefined) return sectionState[key];
    return key.endsWith('_table'); // chart-data tables collapsed by default
  }, [sectionState]);
  const toggleSection = useCallback((key) => {
    setSectionState(prev => {
      const cur = prev[key] !== undefined ? prev[key] : key.endsWith('_table');
      return { ...prev, [key]: !cur };
    });
  }, []);

  // Keep internal tab in sync with controlled prop
  useEffect(() => {
    if (propTab != null) setActiveTab(propTab);
  }, [propTab]);

  const isEditable = true;

  const fetchHistory = () => {
    api.get(`${process.env.REACT_APP_URL}/patients/rom/${patientCard}/history`)
      .then(res => setHistory(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  };

  useEffect(() => {
    if (!patientCard) { setLoading(false); return; }
    Promise.all([
      api.get(`${process.env.REACT_APP_URL}/patients/rom/${patientCard}`).catch(() => null),
      api.get(`${process.env.REACT_APP_URL}/patients/rom/${patientCard}/history`).catch(() => ({ data: [] })),
      api.get(`${process.env.REACT_APP_URL}/bodyparts`).catch(() => ({ data: null })),
      api.get(`${process.env.REACT_APP_URL}/bodyparts/clinical`).catch(() => ({ data: null })),
    ]).then(([mainRes, histRes, bpRes, clinRes]) => {
      const loaded = { rom: {}, muscleStrength: {}, ...(mainRes?.data || {}) };
      setData(loaded);
      setSavedSnapshot(JSON.stringify(loaded));
      setHistory(Array.isArray(histRes?.data) ? histRes.data : []);

      // Use ClinicalConfig from API if available, else fall back to static defaults
      const romPartsData = (clinRes?.data?.romParts?.length > 0) ? clinRes.data.romParts : ROM_PARTS;
      const musclePartsData = (clinRes?.data?.muscleParts?.length > 0) ? clinRes.data.muscleParts : MUSCLE_PARTS;
      setClinicalRomParts(romPartsData);
      setClinicalMuscleParts(musclePartsData);
      const mLookup = buildMuscleLookup(musclePartsData);

      // Build rom-joint icon/bilateral lookup from clinicalConfig
      const romPartLookup = {};
      romPartsData.forEach(rp => { romPartLookup[rp.key] = rp; });

      // Build BPConfig-derived part lists — use annotation romKey + muscles (from backend)
      let romItems = [];
      let mvcItems = [];
      if (bpRes?.data) {
        const allBp = [...(bpRes.data.front || []), ...(bpRes.data.back || [])];
        // Normalise metricType casing (DB may have 'rom', 'Rom', etc.)
        allBp.forEach(a => {
          if (!a.metricType) return;
          const mt = a.metricType.toLowerCase();
          if (mt === 'mvc') a.metricType = 'MVC';
          else if (mt === 'rom') a.metricType = 'ROM';
          else if (mt === 'both') a.metricType = 'both';
          else a.metricType = '';
        });
        const { romKeyMap, musclesMap } = buildBpMaps(allBp);
        allBp.forEach(a => {
          const clinicalKey = romKeyMap[a.key] || '';
          const side = deriveSide(a.key);
          if (a.metricType === 'ROM' || a.metricType === 'both') {
            const romPart = clinicalKey ? romPartLookup[clinicalKey] : null;
            if (romPart) romItems.push({
              key: a.key, partKey: clinicalKey, side,
              label: a.label || (romPart.label + (side ? ` (${side === 'left' ? 'Left' : 'Right'})` : '')),
              icon: romPart.icon,
            });
          }
          if (a.metricType === 'MVC' || a.metricType === 'both') {
            const muscleKeys = musclesMap[a.key] || [];
            if (muscleKeys.length > 0) {
              const romPart = clinicalKey ? romPartLookup[clinicalKey] : null;
              mvcItems.push({
                key: a.key, partKey: a.key, side,
                label: a.label || a.key,
                icon: romPart?.icon || '💪',
                _muscleKeys: muscleKeys,
              });
            }
          }
        });
      }
      setBpRomParts(romItems);
      setBpMvcParts(mvcItems);

      // Pre-select BPConfig parts that already have patient data
      const hasVal = (obj) => {
        if (!obj || typeof obj !== 'object') return false;
        return Object.values(obj).some(v => {
          if (v === null || v === undefined) return false;
          if (typeof v === 'boolean') return v;
          if (typeof v === 'number') return true;
          if (typeof v === 'string') return v.trim() !== '';
          return hasVal(v);
        });
      };
      const autoSelect = (romObj, muscleObj, romItms, mvcItms) => {
        const ps = new Set();
        romItms.forEach(item => {
          const d = romObj?.[item.partKey];
          if (!d) return;
          if (item.side ? Object.values(d).some(mv => hasVal(mv?.[item.side])) : hasVal(d)) ps.add(item.key);
        });
        mvcItms.forEach(item => {
          const d = muscleObj?.[item.partKey];
          if (!d) return;
          if (item.side ? Object.values(d).some(mv => hasVal(mv?.[item.side])) : hasVal(d)) ps.add(item.key);
        });
        return ps;
      };
      let preSelected = autoSelect(loaded.rom, loaded.muscleStrength, romItems, mvcItems);
      if (preSelected.size === 0 && histRes?.data?.length > 0) {
        const latest = [...histRes.data].sort((a, b) => new Date(b.assessmentDate) - new Date(a.assessmentDate))[0];
        preSelected = autoSelect(latest.rom, latest.muscleStrength, romItems, mvcItems);
      }
      if (preSelected.size > 0) setSelectedParts(preSelected);
    }).finally(() => setLoading(false));
  }, [patientCard]);

  const setRom    = (keys, val) => setData(d => ({ ...d, rom:            deepSet(d.rom,            keys, val) }));
  const setMuscle = (keys, val) => setData(d => ({ ...d, muscleStrength: deepSet(d.muscleStrength, keys, val) }));

  // ── Dirty tracking helpers ─────────────────────────────────────────────
  const isPartDirty = useCallback((partKey) => {
    if (!savedSnapshot) return false;
    const saved = JSON.parse(savedSnapshot);
    const curRom = JSON.stringify(data.rom?.[partKey] || {});
    const savRom = JSON.stringify(saved.rom?.[partKey] || {});
    const curMus = JSON.stringify(data.muscleStrength?.[partKey] || {});
    const savMus = JSON.stringify(saved.muscleStrength?.[partKey] || {});
    return curRom !== savRom || curMus !== savMus;
  }, [data, savedSnapshot]);

  const hasAnyUnsaved = useMemo(() => {
    if (!savedSnapshot) return false;
    return JSON.stringify({ rom: data.rom, muscleStrength: data.muscleStrength }) !==
           JSON.stringify(JSON.parse(savedSnapshot));
  }, [data, savedSnapshot]);

  // Warn before page unload if unsaved
  useEffect(() => {
    if (!hasAnyUnsaved) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasAnyUnsaved]);

  // ── Build checkbox list items from BPConfig (ROM or MVC type parts) ──────
  const checkboxItems = useMemo(() => {
    if (activeTab === 'rom') return bpRomParts;
    return bpMvcParts;
  }, [activeTab, bpRomParts, bpMvcParts]);

  // Sorted selected items for the right panel
  const sortedSelected = useMemo(() => {
    return checkboxItems.filter(item => selectedParts.has(item.key));
  }, [checkboxItems, selectedParts]);

  // ── Toggle a part (with unsaved prompt) ────────────────────────────────
  const togglePart = useCallback(async (itemKey, partKey) => {
    if (selectedParts.has(itemKey)) {
      // Deselecting — check if dirty
      if (isPartDirty(partKey)) {
        const ok = await showConfirm(
          'You have unsaved changes for this body part. Deselect anyway? Your edits will NOT be lost (they stay in memory), but please save before leaving the page.',
          'Unsaved Changes'
        );
        if (!ok) return;
      }
    }
    setSelectedParts(prev => {
      const next = new Set(prev);
      if (next.has(itemKey)) next.delete(itemKey);
      else next.add(itemKey);
      return next;
    });
  }, [selectedParts, isPartDirty, showConfirm]);

  // Select / deselect all
  const selectAll = useCallback(() => {
    setSelectedParts(new Set(checkboxItems.map(i => i.key)));
  }, [checkboxItems]);
  const deselectAll = useCallback(async () => {
    if (hasAnyUnsaved) {
      const ok = await showConfirm('You have unsaved changes. Deselect all body parts anyway?', 'Unsaved Changes');
      if (!ok) return;
    }
    setSelectedParts(new Set());
  }, [hasAnyUnsaved, showConfirm]);

  // ── Save ───────────────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    setSaving(true);
    api.post(`${process.env.REACT_APP_URL}/patients/rom/${patientCard}`, { ...data, assessmentType: 'Dedicated', selectedBpKeys: [...selectedParts] })
      .then(() => {
        setSaving(false);
        setSavedSnapshot(JSON.stringify({ rom: data.rom, muscleStrength: data.muscleStrength }));
        showAlert('Assessment saved successfully!', 'success');
        fetchHistory();
      })
      .catch(e => { setSaving(false); showAlert('Save failed: ' + (e?.response?.data || e?.message), 'error'); });
  }, [data, patientCard]); // eslint-disable-line react-hooks/exhaustive-deps

  const btnS = { border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer', padding: '10px 24px', transition: 'all 0.18s' };

  return (
    <div style={{ borderRadius: 16, overflow: 'hidden', border: '1.5px solid #e8edf5', boxShadow: '0 4px 20px rgba(0,0,0,.08)', background: '#fff' }}>
      {dialogNode}

      {/* ── Header with RomImg ─────────────────────────────────────────── */}
      <div style={{ position: 'relative', overflow: 'hidden', background: '#1a1c2e' }}>
        <img
          src="/RomImg.jpeg" alt="ROM Assessment"
          style={{ width: '47%', height: 'auto', display: 'block', margin: '0 auto', filter: 'brightness(0.45)' }}
        />
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px', gap: 16, flexWrap: 'wrap',
        }}>
          {onClose && (
            <button onClick={onClose} style={{
              width: 36, height: 36, borderRadius: '50%', border: 'none',
              background: 'rgba(255,255,255,.2)', color: '#fff', fontSize: 18,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>✕</button>
          )}
        </div>
      </div>

      {/* ── Inner tab bar ─────────────────────────────────────────────── */}
      {showTabBar && (
        <div style={{ display: 'flex', borderBottom: '2px solid #e8edf5', background: '#fff', padding: '0 20px' }}>
          {[
            { key: 'rom',    label: '📐 Range of Motion (ROM)',  activeColor: '#1976d2' },
            { key: 'muscle', label: '💪 Muscle Strength (MVC)',  activeColor: '#7b1fa2' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              padding: '13px 26px', border: 'none', background: 'transparent',
              borderBottom: activeTab === tab.key ? `3px solid ${tab.activeColor}` : '3px solid transparent',
              color: activeTab === tab.key ? tab.activeColor : '#6b7280',
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
              marginBottom: -2, transition: 'all 0.18s',
            }}>{tab.label}</button>
          ))}
        </div>
      )}

      {/* ── Content: Left menu + Right readings ──────────────────────── */}
      <div style={{ padding: '0', background: '#f8fafc', minHeight: 280 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af', fontSize: 16 }}>Loading assessment data...</div>
        ) : (
          <div style={{ display: 'flex', minHeight: 500 }}>
            {/* ── LEFT: Body part checkbox menu ── */}
            <div style={{
              width: 240, minWidth: 240, borderRight: '1.5px solid #e8edf5', background: '#fff',
              overflowY: 'auto', padding: '12px 0',
            }}>
              <div style={{ padding: '0 14px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>Body Parts</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={selectAll} style={{ fontSize: 10, color: '#1976d2', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>All</button>
                  <button onClick={deselectAll} style={{ fontSize: 10, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>None</button>
                </div>
              </div>
              {checkboxItems.length === 0 && (
                <div style={{ padding: '16px 14px', fontSize: 11, color: '#9ca3af', textAlign: 'center', lineHeight: 1.5 }}>
                  No {activeTab === 'rom' ? 'ROM' : 'MVC'} body parts configured.<br/>
                  <span style={{ color: '#3b82f6' }}>Go to Body Parts Config</span> and set metricType to {activeTab === 'rom' ? 'ROM (or Both)' : 'MVC (or Both)'}.
                </div>
              )}
              {checkboxItems.map(item => {
                const checked = selectedParts.has(item.key);
                const dirty = isPartDirty(item.partKey);
                return (
                  <label
                    key={item.key}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 14px', cursor: 'pointer',
                      background: checked ? '#eff6ff' : 'transparent',
                      borderLeft: checked ? '3px solid #1976d2' : '3px solid transparent',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePart(item.key, item.partKey)}
                      style={{ width: 16, height: 16, accentColor: '#1976d2', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 15, flexShrink: 0 }}>{item.icon}</span>
                    <span style={{
                      fontSize: 13, fontWeight: checked ? 600 : 400,
                      color: dirty ? '#d97706' : (checked ? '#1a1d23' : '#555'),
                    }}>
                      {item.label}
                    </span>
                    {dirty && (
                      <span style={{
                        marginLeft: 'auto', fontSize: 9, fontWeight: 700,
                        color: '#d97706', background: '#fef3c7', borderRadius: 8,
                        padding: '1px 6px', flexShrink: 0,
                      }}>EDITED</span>
                    )}
                  </label>
                );
              })}

              {/* Info banner in left panel */}
              {activeTab === 'rom' && (
                <div style={{ margin: '12px 10px', padding: '8px 10px', background: '#e0f2fe', borderRadius: 10, fontSize: 10, color: '#0369a1', fontWeight: 500, border: '1px solid #bae6fd' }}>
                  <strong>Active</strong> = patient &nbsp;|&nbsp;
                  <strong>Passive</strong> = therapist &nbsp;|&nbsp;
                  <strong>Pain</strong> = pain present
                </div>
              )}
              {activeTab === 'muscle' && (
                <div style={{ margin: '12px 10px', padding: '8px 10px', background: '#f5f3ff', borderRadius: 10, border: '1px solid #ddd6fe', fontSize: 10, color: '#6d28d9' }}>
                  <strong>MVC 0–5:</strong> 0=No contraction, 5=Normal
                </div>
              )}
            </div>

            {/* ── RIGHT: Selected parts readings ── */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              {/* Unsaved changes banner */}
              {hasAnyUnsaved && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
                  padding: '10px 16px', background: '#fef3c7', borderRadius: 12,
                  border: '1.5px solid #fcd34d', fontSize: 13, color: '#92400e', fontWeight: 600,
                }}>
                  <span style={{ fontSize: 16 }}>⚠️</span>
                  You have unsaved changes. Click <strong style={{ margin: '0 3px' }}>Save</strong> before leaving this page.
                </div>
              )}

              {sortedSelected.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af', fontSize: 13 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>☑️</div>
                  Select body parts from the left menu to start assessment
                </div>
              ) : (
                sortedSelected.map((item, itemIdx) => {
                  // For ROM tab: look up clinical ROM part; for MVC: no part needed (uses muscles from BPConfig)
                  const part = activeTab === 'rom'
                    ? clinicalRomParts.find(p => p.key === item.partKey)
                    : { key: item.partKey, label: item.label, icon: item.icon }; // placeholder for MVC
                  if (activeTab === 'rom' && !part) return null;
                  const idx = itemIdx;
                  const dirty = isPartDirty(item.partKey);
                  const color = PART_COLORS[idx % PART_COLORS.length];
                  const sectionBorder = dirty ? '2px solid #f59e0b' : `1.5px solid ${color}50`;
                  const sectionShadow = dirty
                    ? '0 0 0 2px rgba(245,158,11,0.18), 0 4px 16px rgba(0,0,0,.10)'
                    : `0 1px 0 ${color}18, 0 4px 14px rgba(0,0,0,.10), 0 8px 24px rgba(0,0,0,.06)`;

                  if (activeTab === 'rom') {
                    // For bilateral parts being shown as left/right, filter display
                    const showSide = part.bilateral ? item.side : null;
                    const dataKey = `${item.key}_data`, chartKey = `${item.key}_chart`, tableKey = `${item.key}_table`;
                    return (
                      <div key={item.key} style={{ marginBottom: 28, borderRadius: 16, border: sectionBorder, overflow: 'hidden', boxShadow: sectionShadow }}>
                        <div style={{
                          background: `linear-gradient(135deg, ${color}30, ${color}12)`,
                          borderBottom: `2.5px solid ${color}40`,
                          padding: '11px 16px', display: 'flex', alignItems: 'center', gap: 10,
                        }}>
                          <span style={{ fontSize: 22, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.15))' }}>{part.icon}</span>
                          <span style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', textShadow: '0 1px 0 rgba(255,255,255,.6)' }}>
                            {part.label}
                            {showSide && <span style={{ fontWeight: 600, color: '#374151' }}> — {showSide === 'left' ? 'Left' : 'Right'}</span>}
                          </span>
                          {dirty && (
                            <span style={{ fontSize: 11, background: '#fef3c7', color: '#d97706', borderRadius: 12, padding: '2px 10px', fontWeight: 700, marginLeft: 4 }}>UNSAVED</span>
                          )}
                        </div>

                        {/* Sub-section 1: Data Collection */}
                        <div onClick={() => toggleSection(dataKey)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 16px', cursor: 'pointer', background: `linear-gradient(90deg, ${color}08, #f7f9fc)`, borderBottom: '1px solid #e8edf5', userSelect: 'none' }}>
                          <span style={{ fontSize: 9, display: 'inline-block', transition: 'transform 0.15s', transform: isSectionCollapsed(dataKey) ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▼</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#0d47a1' }}>Readings</span>
                        </div>
                        {!isSectionCollapsed(dataKey) && (
                          <>
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                  <tr style={{ background: '#f7f9fc' }}>
                                    <th style={{ ...thS, minWidth: 160 }}>Movement</th>
                                    <th style={{ ...thS, textAlign: 'center', color: '#9ca3af' }}>Normal</th>
                                    <th style={{ ...thS, textAlign: 'center', color: showSide === 'right' ? '#b91c1c' : '#1d4ed8' }}>Active°</th>
                                    <th style={{ ...thS, textAlign: 'center', color: showSide === 'right' ? '#b91c1c' : '#1d4ed8' }}>Passive°</th>
                                    <th style={{ ...thS, textAlign: 'center' }}>Pain</th>
                                    <th style={{ ...thS, minWidth: 140 }}>Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {part.movements.map((move, mi) => {
                                    const mv = (data.rom?.[part.key]?.[move.key]) || {};
                                    const sideData = showSide ? (mv[showSide] || {}) : mv;
                                    const basePath = showSide
                                      ? (...p) => val => setRom([part.key, move.key, showSide, ...p], val)
                                      : (...p) => val => setRom([part.key, move.key, ...p], val);
                                    return (
                                      <tr key={move.key} style={{ background: mi % 2 ? '#fafbfd' : '#fff' }}>
                                        <td style={{ ...tdS, fontWeight: 500, color: '#374151' }}>{move.label}</td>
                                        <td style={{ ...tdS, textAlign: 'center', color: '#9ca3af', fontWeight: 600 }}>{move.normal}°</td>
                                        <td style={{ ...tdS, textAlign: 'center', background: showSide === 'right' ? '#fff1f2' : '#eff6ff' }}>
                                          <NumInput val={sideData.active} onChange={basePath('active')} editable={isEditable} />
                                        </td>
                                        <td style={{ ...tdS, textAlign: 'center', background: showSide === 'right' ? '#fff1f2' : '#eff6ff' }}>
                                          <NumInput val={sideData.passive} onChange={basePath('passive')} editable={isEditable} />
                                        </td>
                                        <td style={{ ...tdS, textAlign: 'center' }}>
                                          <PainCheck val={sideData.pain} onChange={basePath('pain')} editable={isEditable} />
                                        </td>
                                        <td style={tdS}>
                                          {isEditable ? (
                                            <input
                                              type="text" value={sideData.notes || ''}
                                              onChange={e => basePath('notes')(e.target.value)}
                                              placeholder="Notes..."
                                              style={{ width: '100%', minWidth: 110, border: '1.5px solid #e2e8f0', borderRadius: 6, padding: '3px 8px', fontSize: 12, background: '#f8fafc', outline: 'none' }}
                                            />
                                          ) : (
                                            <span style={{ color: '#6b7280', fontSize: 12 }}>{sideData.notes || ''}</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            {/* Comments box */}
                            <div style={{ padding: '10px 16px', borderTop: '1px solid #e8edf5' }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Comments</div>
                              {isEditable ? (
                                <textarea
                                  value={data.rom?.[part.key]?._comments || ''}
                                  onChange={e => setRom([part.key, '_comments'], e.target.value)}
                                  placeholder="Add observations, clinical notes..."
                                  rows={2}
                                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', fontSize: 12, background: '#f8fafc', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                                />
                              ) : (
                                <div style={{ fontSize: 12, color: '#374151', minHeight: 20 }}>{data.rom?.[part.key]?._comments || '—'}</div>
                              )}
                            </div>
                          </>
                        )}

                        <PartHistoryChart history={history} partKey={item.partKey} side={item.side} activeTab="rom" romParts={clinicalRomParts} muscleParts={clinicalMuscleParts} chartKey={chartKey} tableKey={tableKey} isSectionCollapsed={isSectionCollapsed} toggleSection={toggleSection} />
                      </div>
                    );
                  } else {
                    // Muscle strength — use muscles from BPConfig annotation (set via backend)
                    const bpKey = item.key; // e.g. 'knee_L'
                    const mLookup = buildMuscleLookup(clinicalMuscleParts);
                    const muscleKeys = item._muscleKeys || [];
                    const muscles = muscleKeys.map(mk => mLookup[mk]).filter(Boolean);
                    const partComments = data.muscleStrength?.[bpKey]?._comments || '';
                    const dataKey = `${item.key}_data`, chartKey = `${item.key}_chart`, tableKey = `${item.key}_table`;

                    return (
                      <div key={item.key} style={{ marginBottom: 28, borderRadius: 16, border: sectionBorder, overflow: 'hidden', boxShadow: sectionShadow }}>
                        <div style={{
                          background: `linear-gradient(135deg, ${color}30, ${color}12)`,
                          borderBottom: `2.5px solid ${color}40`,
                          padding: '11px 16px', display: 'flex', alignItems: 'center', gap: 10,
                        }}>
                          <span style={{ fontSize: 22, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.15))' }}>{item.icon}</span>
                          <span style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', textShadow: '0 1px 0 rgba(255,255,255,.6)' }}>
                            {item.label}
                            {item.side && <span style={{ fontWeight: 600, color: '#374151' }}> — {item.side === 'left' ? 'Left' : 'Right'}</span>}
                          </span>
                          <span style={{ fontSize: 9, background: '#e9d5ff', color: '#581c87', borderRadius: 12, padding: '2px 9px', fontWeight: 800, border: '1px solid #c084fc' }}>MVC</span>
                          {dirty && (
                            <span style={{ fontSize: 11, background: '#fef3c7', color: '#d97706', borderRadius: 12, padding: '2px 10px', fontWeight: 700, marginLeft: 4 }}>UNSAVED</span>
                          )}
                        </div>

                        {/* Sub-section 1: Data Collection */}
                        <div onClick={() => toggleSection(dataKey)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 16px', cursor: 'pointer', background: `linear-gradient(90deg, ${color}08, #f7f9fc)`, borderBottom: '1px solid #e8edf5', userSelect: 'none' }}>
                          <span style={{ fontSize: 9, display: 'inline-block', transition: 'transform 0.15s', transform: isSectionCollapsed(dataKey) ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▼</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#581c87' }}>Readings</span>
                        </div>
                        {!isSectionCollapsed(dataKey) && (
                          <>
                            {muscles.length === 0 ? (
                              <div style={{ padding: '16px 20px', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No muscles mapped for this body part.</div>
                            ) : (
                              <div style={{ overflowX: 'auto', padding: '0 4px' }}>
                                {muscles.map(muscle => (
                                  <div key={muscle.key} style={{ marginBottom: 4 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#7b1fa2', padding: '8px 12px 4px', borderBottom: '1px solid #f0e6ff' }}>
                                      {muscle.icon} {muscle.label}
                                      {muscle.bilateral && <span style={{ fontSize: 9, color: '#9ca3af', marginLeft: 6 }}>bilateral</span>}
                                    </div>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                      <thead>
                                        <tr style={{ background: '#f7f9fc' }}>
                                          <th style={{ ...thS, minWidth: 140 }}>Subgroup</th>
                                          <th style={{ ...thS, textAlign: 'center', minWidth: 55 }}>Grade (0-5)</th>
                                          <th style={{ ...thS, textAlign: 'center', minWidth: 100 }}>Str (N)</th>
                                          <th style={{ ...thS, textAlign: 'center', minWidth: 90 }}>Contraction</th>
                                          <th style={{ ...thS, textAlign: 'center', minWidth: 70 }}>Endur (s)</th>
                                          <th style={{ ...thS, textAlign: 'center', minWidth: 60 }}>Fatigue</th>
                                          <th style={{ ...thS, textAlign: 'center', minWidth: 60, color: '#dc2626' }}>Pain</th>
                                          <th style={{ ...thS, textAlign: 'center', minWidth: 60, color: '#d97706' }}>Spasm</th>
                                          <th style={{ ...thS, minWidth: 100 }}>Notes</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {muscle.groups.map((grp, gi) => {
                                          const gd = data.muscleStrength?.[bpKey]?.[muscle.key]?.[grp.key] || {};
                                          const setF = (field, val) => setMuscle([bpKey, muscle.key, grp.key, field], val);
                                          return (
                                            <tr key={grp.key} style={{ background: gi % 2 ? '#fafbfd' : '#fff' }}>
                                              <td style={{ ...tdS, fontWeight: 500, color: '#374151' }}>{grp.label}</td>
                                              <td style={{ ...tdS, textAlign: 'center' }}>
                                                <GradeSelect val={gd.grade} onChange={v => setF('grade', v)} editable={isEditable} />
                                              </td>
                                              <td style={{ ...tdS, textAlign: 'center' }}>
                                                {isEditable ? (
                                                  <input type="number" min="0" max="999.99" step="0.01" value={gd.strength ?? ''}
                                                    onChange={e => setF('strength', e.target.value === '' ? undefined : Number(e.target.value))}
                                                    style={{ width: 72, border: '1.5px solid #e2e8f0', borderRadius: 5, padding: '2px 4px', fontSize: 12, textAlign: 'center', background: '#f8fafc', outline: 'none' }}
                                                  />
                                                ) : <span style={{ color: '#374151' }}>{gd.strength != null ? `${gd.strength}N` : '—'}</span>}
                                              </td>
                                              <td style={{ ...tdS, textAlign: 'center' }}>
                                                {isEditable ? (
                                                  <select value={gd.contraction || ''} onChange={e => setF('contraction', e.target.value)}
                                                    style={{ border: '1.5px solid #e2e8f0', borderRadius: 5, padding: '2px 4px', fontSize: 11, background: '#f8fafc', outline: 'none', cursor: 'pointer' }}>
                                                    <option value="">—</option>
                                                    <option value="Normal">Normal</option><option value="Weak">Weak</option>
                                                    <option value="Tremor">Tremor</option><option value="None">None</option>
                                                  </select>
                                                ) : <span style={{ color: '#374151' }}>{gd.contraction || '—'}</span>}
                                              </td>
                                              <td style={{ ...tdS, textAlign: 'center' }}>
                                                {isEditable ? (
                                                  <input type="number" min="0" max="999" value={gd.endurance ?? ''}
                                                    onChange={e => setF('endurance', e.target.value === '' ? undefined : Number(e.target.value))}
                                                    style={{ width: 52, border: '1.5px solid #e2e8f0', borderRadius: 5, padding: '2px 4px', fontSize: 12, textAlign: 'center', background: '#f8fafc', outline: 'none' }}
                                                  />
                                                ) : <span style={{ color: '#374151' }}>{gd.endurance != null ? `${gd.endurance}s` : '—'}</span>}
                                              </td>
                                              <td style={{ ...tdS, textAlign: 'center' }}>
                                                {isEditable ? (
                                                  <select value={gd.fatigue || ''} onChange={e => setF('fatigue', e.target.value)}
                                                    style={{ border: '1.5px solid #e2e8f0', borderRadius: 5, padding: '2px 4px', fontSize: 11, background: '#f8fafc', outline: 'none', cursor: 'pointer', width: 42 }}>
                                                    <option value="">—</option><option value="Y">Y</option><option value="N">N</option>
                                                  </select>
                                                ) : <span style={{ color: '#374151' }}>{gd.fatigue || '—'}</span>}
                                              </td>
                                              <td style={{ ...tdS, textAlign: 'center' }}>
                                                {isEditable ? (
                                                  <input type="number" min="0" max="10" value={gd.pain ?? ''}
                                                    onChange={e => setF('pain', e.target.value === '' ? undefined : Number(e.target.value))}
                                                    style={{ width: 40, border: '1.5px solid #fecdd3', borderRadius: 5, padding: '2px 4px', fontSize: 12, textAlign: 'center', background: '#fff1f2', color: '#dc2626', outline: 'none' }}
                                                  />
                                                ) : <span style={{ color: '#dc2626' }}>{gd.pain != null ? `${gd.pain}/10` : '—'}</span>}
                                              </td>
                                              <td style={{ ...tdS, textAlign: 'center' }}>
                                                {isEditable ? (
                                                  <select value={gd.spasm || ''} onChange={e => setF('spasm', e.target.value)}
                                                    style={{ border: '1.5px solid #fde68a', borderRadius: 5, padding: '2px 4px', fontSize: 11, background: '#fffbeb', color: '#d97706', outline: 'none', cursor: 'pointer', width: 42 }}>
                                                    <option value="">—</option><option value="Y">Y</option><option value="N">N</option>
                                                  </select>
                                                ) : <span style={{ color: '#d97706' }}>{gd.spasm || '—'}</span>}
                                              </td>
                                              <td style={tdS}>
                                                {isEditable ? (
                                                  <input type="text" value={gd.notes || ''}
                                                    onChange={e => setF('notes', e.target.value)}
                                                    placeholder="..."
                                                    style={{ width: '100%', minWidth: 80, border: '1.5px solid #e2e8f0', borderRadius: 5, padding: '2px 6px', fontSize: 11, background: '#f8fafc', outline: 'none' }}
                                                  />
                                                ) : <span style={{ color: '#6b7280', fontSize: 11 }}>{gd.notes || ''}</span>}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Comments box */}
                            <div style={{ padding: '10px 16px', borderTop: '1px solid #e8edf5' }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Comments</div>
                              {isEditable ? (
                                <textarea
                                  value={partComments}
                                  onChange={e => setMuscle([bpKey, '_comments'], e.target.value)}
                                  placeholder="Add observations, clinical notes..."
                                  rows={2}
                                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', fontSize: 12, background: '#f8fafc', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                                />
                              ) : (
                                <div style={{ fontSize: 12, color: '#374151', minHeight: 20 }}>{partComments || '—'}</div>
                              )}
                            </div>
                          </>
                        )}

                        <PartHistoryChart history={history} partKey={item.partKey} side={item.side} activeTab="muscle" romParts={clinicalRomParts} muscleParts={clinicalMuscleParts} muscleKeys={item._muscleKeys} chartKey={chartKey} tableKey={tableKey} isSectionCollapsed={isSectionCollapsed} toggleSection={toggleSection} />
                      </div>
                    );
                  }
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <div style={{
        padding: '14px 24px', background: '#fff',
        borderTop: '2px solid #e8edf5',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10,
      }}>
        <div style={{ fontSize: 13, color: '#9ca3af' }}>
          📋 {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          {selectedParts.size > 0 && <span style={{ marginLeft: 8, color: '#1976d2', fontWeight: 600 }}>{selectedParts.size} part(s) selected</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {onClose && (
            <button onClick={onClose} style={{ ...btnS, background: '#f1f5f9', color: '#475569' }}>Cancel</button>
          )}
        </div>
      </div>

      {/* ── Floating Save button ──────────────────────────────────────── */}
      {isEditable && (
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            position: 'fixed', right: 20, bottom: 80, zIndex: 1000,
            background: saving ? '#94a3b8' : hasAnyUnsaved ? 'linear-gradient(135deg, #d97706, #b45309)' : 'linear-gradient(135deg, #1976d2, #0d47a1)',
            color: '#fff', border: 'none', borderRadius: 26,
            padding: '12px 22px', fontSize: 13, fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer',
            boxShadow: saving ? 'none' : hasAnyUnsaved ? '0 4px 18px rgba(217,119,6,0.5)' : '0 4px 18px rgba(25,118,210,0.45)',
            transition: 'all 0.2s', whiteSpace: 'nowrap',
            animation: hasAnyUnsaved ? 'pulse 2s infinite' : 'none',
          }}
        >
          {saving ? 'Saving...' : hasAnyUnsaved ? 'Save Changes' : 'Save'}
        </button>
      )}
    </div>
  );
}

// ── Default export: modal wrapper (use this outside PatientDashboard) ─────────
export default function RomAssessment({ patientCard, patientName, onClose }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose?.()}>
      <div style={{ maxWidth: 1100, width: '96vw', maxHeight: '93vh', overflowY: 'auto', borderRadius: 20 }}>
        <RomAssessmentPanel patientCard={patientCard} patientName={patientName} onClose={onClose} />
      </div>
    </div>
  );
}
